// Документът на инвентаризация отвсякъде (началният екран, справката):
// модалът, анулирането с причина и редакцията. Връща `open` (+ `openById`) и
// модалите за рендер.
import { useState } from "react";
import { App } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getStocktake } from "../../../../api/inventory";
import type { StocktakeDocument } from "../../../../api/inventoryStocktakes";
import { useAnnulStocktake } from "../../../../hooks/useInventoryStocktakes";
import { AnnulDocumentModal } from "../../components/AnnulDocumentModal";
import {
  annulUndoNote,
  productLineItems,
  useProductById,
} from "../../components/confirm/confirmPreviews";
import { useActionToast } from "../../components/useActionToast";
import { StocktakeDocumentModal } from "./StocktakeDocumentModal";

type OpenOptions = {
  // Анулирай/Редактирай — само при отваряне от ред (не след запис)
  editable?: boolean;
  autoPrint?: boolean;
};

export const useStocktakeDocument = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const showToast = useActionToast();
  const annulMutation = useAnnulStocktake();
  const productById = useProductById();
  const [doc, setDoc] = useState<{
    stocktake: StocktakeDocument;
    editable: boolean;
    autoPrint: boolean;
  } | null>(null);
  const [annul, setAnnul] = useState<StocktakeDocument | null>(null);

  // Моментално (записът от списъка), после ъпгрейд до пълния документ (със
  // Търсено/Намерено на редовете). Мета полетата (анулиран…) идват от реда.
  const open = (stocktake: StocktakeDocument, options?: OpenOptions) => {
    setDoc({
      stocktake,
      editable: options?.editable ?? true,
      autoPrint: options?.autoPrint ?? false,
    });
    getStocktake(stocktake.id)
      .then((full) =>
        setDoc((current) =>
          current && current.stocktake.id === full.id
            ? { ...current, stocktake: { ...current.stocktake, ...full } }
            : current,
        ),
      )
      .catch(() => {
        /* остава версията от реда */
      });
  };

  const openById = (id: number, options?: OpenOptions) =>
    getStocktake(id)
      .then((stocktake) =>
        setDoc({
          stocktake,
          editable: options?.editable ?? true,
          autoPrint: options?.autoPrint ?? false,
        }),
      )
      .catch(() => message.error(t("inventoryPage.errLoad")));

  const edit = (stocktake: StocktakeDocument) => {
    setDoc(null);
    navigate(`/inventory/stocktake/${stocktake.id}/edit`);
  };

  const confirmAnnul = (reason: string | null) => {
    if (!annul) return;
    annulMutation.mutate(
      { id: annul.id, reason },
      {
        onSuccess: () => {
          showToast({ text: t("inventoryPage.annulledToast", { number: annul.id }) });
          setAnnul(null);
          setDoc(null);
        },
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { detail?: string } } };
          message.error(err.response?.data?.detail || t("inventoryPage.errDelete"));
        },
      },
    );
  };

  const modals = (
    <>
      <StocktakeDocumentModal
        open={!!doc}
        stocktake={doc?.stocktake ?? null}
        autoPrint={doc?.autoPrint}
        onClose={() => setDoc(null)}
        onEdit={doc?.editable ? () => edit(doc.stocktake) : undefined}
        onAnnul={doc?.editable ? () => setAnnul(doc.stocktake) : undefined}
      />
      <AnnulDocumentModal
        open={!!annul}
        title={t("inventoryPage.cfmAnnulStocktakeTitle", { number: annul?.id ?? "" })}
        text={t("inventoryPage.stocktakeAnnulText")}
        confirmLabel={t("inventoryPage.cfmAnnulStocktakeConfirm")}
        preview={
          annul
            ? {
                caption: t("inventoryPage.cfmAnnulStocktakeCaption"),
                items: productLineItems(
                  (annul.lines ?? [])
                    .filter((line) => line.diff)
                    .map((line) => ({ ...line, quantity: line.diff })),
                  productById,
                  { signed: true },
                ),
              }
            : undefined
        }
        note={annulUndoNote(t)}
        saving={annulMutation.isPending}
        onCancel={() => setAnnul(null)}
        onConfirm={confirmAnnul}
      />
    </>
  );

  return {
    open,
    openById,
    edit,
    requestAnnul: (stocktake: StocktakeDocument) => setAnnul(stocktake),
    modals,
  };
};
